import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { Mic, MicOff, Video, VideoOff, Crown } from "lucide-react";
import { useTracks, useParticipants, useLocalParticipant, useLocalParticipantPermissions, VideoTrack } from "@livekit/components-react";
import { Track } from "livekit-client";
import { Avatar } from "@/components/Brand";

function Tile({ s, p, cam, isMod, onRemove, meId }) {
  const speaking = p?.isSpeaking;
  return (
    <div className={`relative overflow-hidden rounded-2xl bg-card ring-1 ring-white/5 ${speaking ? "speaking ring-emerald-500" : ""}`} data-testid={`stage-tile-${s.user_id}`}>
      {cam ? (
        <VideoTrack trackRef={cam} className={`absolute inset-0 h-full w-full object-cover ${s.user_id === meId ? "-scale-x-100" : ""}`} data-testid={`stage-video-${s.user_id}`} />
      ) : (
        <div className="absolute inset-0 grid place-items-center bg-gradient-to-br from-indigo-500/25 to-amber-500/15">
          <div className="text-center">
            <Avatar name={s.name} src={s.avatar} size={72} className="mx-auto text-xl bg-white/10" />
            <p className="mt-2 text-[11px] text-white/60">{p ? "Câmera desligada" : "Aguardando conexão"}</p>
          </div>
        </div>
      )}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
      <div className="absolute bottom-2 left-2 right-2 flex items-center gap-1.5">
        <span className={`grid h-6 w-6 place-items-center rounded-full ${speaking ? "bg-emerald-500" : "bg-black/60"}`}>
          {p?.isMicrophoneEnabled ? <Mic size={12} /> : <MicOff size={12} className="text-white/60" />}
        </span>
        <span className="truncate text-xs font-bold">{s.name}{s.user_id === meId ? " (você)" : ""}</span>
        {s.role === "host" && <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-[hsl(var(--gold))] px-2 py-0.5 text-[10px] font-extrabold text-[#0B0F17]"><Crown size={10} /> Anfitrião</span>}
      </div>
      {isMod && s.role !== "host" && (
        <button onClick={() => onRemove(s.user_id)} className="absolute right-2 top-2 rounded-full bg-black/60 px-2 py-1 text-[10px] font-bold hover:bg-red-600 transition-colors" data-testid={`stage-remove-${s.user_id}`}>Remover</button>
      )}
    </div>
  );
}

export function LiveStage({ stage, isMod, meId, onRemove }) {
  const cams = useTracks([{ source: Track.Source.Camera, withPlaceholder: false }]);
  const participants = useParticipants();
  const byId = Object.fromEntries(participants.map((p) => [p.identity, p]));
  const camById = Object.fromEntries(cams.map((t) => [t.participant.identity, t]));
  const shown = stage.filter((s) => !s.simulated || s.role === "host");
  const n = shown.length;
  const cols = n <= 1 ? "grid-cols-1" : n <= 4 ? "grid-cols-2" : "grid-cols-3";
  return (
    <div className={`grid ${cols} gap-2 h-full auto-rows-fr`} data-testid="live-room-stage-grid">
      {shown.map((s) => <Tile key={s.user_id} s={s} p={byId[s.user_id]} cam={camById[s.user_id]} isMod={isMod} onRemove={onRemove} meId={meId} />)}
    </div>
  );
}

export function MediaControls({ onStage, onNeedToken }) {
  const { localParticipant, isCameraEnabled, isMicrophoneEnabled } = useLocalParticipant();
  const perms = useLocalParticipantPermissions();
  const canPublish = !!perms?.canPublish;
  const asked = useRef(false);

  useEffect(() => {
    if (onStage && canPublish && !asked.current) {
      asked.current = true;
      localParticipant.enableCameraAndMicrophone().catch(() => toast.error("Permita o acesso à câmera e ao microfone no navegador"));
    }
    if (!onStage) {
      asked.current = false;
      localParticipant.setCameraEnabled(false).catch(() => {});
      localParticipant.setMicrophoneEnabled(false).catch(() => {});
    }
  }, [onStage, canPublish, localParticipant]);

  useEffect(() => {
    if (!onStage || canPublish) return;
    const t = setTimeout(onNeedToken, 3000);
    return () => clearTimeout(t);
  }, [onStage, canPublish, onNeedToken]);

  if (!onStage) return null;
  const btn = "grid h-11 w-11 place-items-center rounded-full ring-1 ring-white/10 transition-colors";
  return (
    <div className="flex gap-2" data-testid="media-controls">
      <button onClick={() => localParticipant.setMicrophoneEnabled(!isMicrophoneEnabled)} disabled={!canPublish} className={`${btn} ${isMicrophoneEnabled ? "bg-white/10" : "bg-red-600"}`} data-testid="toggle-mic-button" aria-label="Microfone">
        {isMicrophoneEnabled ? <Mic size={18} /> : <MicOff size={18} />}
      </button>
      <button onClick={() => localParticipant.setCameraEnabled(!isCameraEnabled)} disabled={!canPublish} className={`${btn} ${isCameraEnabled ? "bg-white/10" : "bg-red-600"}`} data-testid="toggle-camera-button" aria-label="Câmera">
        {isCameraEnabled ? <Video size={18} /> : <VideoOff size={18} />}
      </button>
    </div>
  );
}
